
import React, { Component } from 'react'
import { StaticQuery, graphql } from 'gatsby'
import PropTypes from 'prop-types'
import ArticleGrid from '../ArticleGrid'
import Container from '../../Container'
import { ArticleContainer,
  ArticleFilterTrigger,
  ArticleFilterContainer,
  ButtonContainer,
  ArticleFilterButton,
  Error } from './ArticleGridContainer.style'
import { LoadMoreButton, ButtonArrow } from '../../../shared/Buttons/Buttons.styles'
import ArrowRight from '../Icons/ArrowRight'
import FilterItem from '../FilterItem'
import FilterArrow from '../Icons/FilterArrow'
import { removeDuplicates, isMobile } from '../../../utils/siteHelper'
import { ARTICLE_FILTER_TRIGGER,
  RESET_FILTER_TEXT,
  YEAR_FILTER_TITLE,
  CATEGORY_FILTER_TITLE,
  NO_ARTICLES_FOUND } from '../../../locales'

class ArticleItems extends Component {
  constructor(props) {
    super(props)
    this.state = {
      articleList: [],
      visibleItems: [],
      activeFilters: [],
      filteredItems: [],
      filterOpen: false,
      count: 1,
      showLoadMore: true,
      loadMore: {
        isEnable: true,
        preloadItems: 9,
      },
    }

    this.addFilter = this.addFilter.bind(this)
  }

  componentDidMount() {
    const { articleList } = this.props
    const { loadMore } = this.state

    this.setState({
      articleList: articleList.edges,
      visibleItems: articleList.edges.slice(0, loadMore.preloadItems),
    })
  }

  loadMore = () => {
    const { articleList, filteredItems, loadMore } = this.state
    let { count } = this.state

    this.setState({
      count: ++count,
      visibleItems: filteredItems.length ? filteredItems.slice(0, loadMore.preloadItems * count) : articleList.slice(0, loadMore.preloadItems * count),
    })
  }

  addFilter = (e, filter) => {
    e.stopPropagation()
    const { articleList, loadMore, activeFilters } = this.state
    // Remove duplicates so if user enters multiple years/cats, it gets filtered out
    const newFilter = removeDuplicates([filter, ...activeFilters], 'type')
    let availableItems = articleList

    // Apply filters
    newFilter.forEach((filter) => {
      if (filter.type === 'date') {
        availableItems = availableItems.filter((article) => {
          return parseInt(article.node.publishYear) === parseInt(filter.value)
        })
      } else if (filter.type === 'category') {
        availableItems = availableItems.filter((article) => {
          return article.node.categories.title === filter.value
        })
      }
    })

    // Move to state and hide the load more button if there are not enough items
    this.setState({
      activeFilters: newFilter,
      showLoadMore: availableItems.length > loadMore.preloadItems,
      filteredItems: availableItems,
      visibleItems: availableItems.slice(0, loadMore.preloadItems),
    })
  }

  clearFilter = (e) => {
    e.stopPropagation()
    const { articleList, loadMore } = this.state

    this.setState({
      activeFilters: [],
      visibleItems: articleList.slice(0, loadMore.preloadItems),
      filteredItems: articleList,
      showLoadMore: true,
    })
  }

  openFilter = () => {
    this.setState(prevState => ({
      filterOpen: !prevState.filterOpen,
    }))
  }

  render() {
    const { articleList, visibleItems, showLoadMore, activeFilters, filteredItems, filterOpen } = this.state
    const { loadMoreLabel, dateFilter, categoryFilter } = this.props

    return (
      <Container>
        { isMobile() && (
          <ArticleFilterTrigger
            open={isMobile() && filterOpen}
            onClick={this.openFilter}
          >
            {ARTICLE_FILTER_TRIGGER}
            <svg viewBox="0 0 22 10">
              <FilterArrow />
            </svg>
          </ArticleFilterTrigger>
        )}
        <ArticleFilterContainer
          // Only add prop if it's mobile otherwise ignore
          open={isMobile() && filterOpen}
        >
          <FilterItem
            title={CATEGORY_FILTER_TITLE}
            items={categoryFilter}
            addFilter={this.addFilter}
            activeFilter={activeFilters.find(obj => { return obj.type === 'category' })}
            clearFilter={this.clearFilter}
          />
          <FilterItem
            title={YEAR_FILTER_TITLE}
            items={dateFilter}
            addFilter={this.addFilter}
            activeFilter={activeFilters.find(obj => { return obj.type === 'date' })}
          />
          { isMobile() && (
            <ButtonContainer>
              <ArticleFilterButton onClick={this.clearFilter}>
                <button type="button">
                  {RESET_FILTER_TEXT}
                </button>
              </ArticleFilterButton>
            </ButtonContainer>
          )}
        </ArticleFilterContainer>
        <ArticleContainer>
          { visibleItems.length === 0
            ? <Error>{NO_ARTICLES_FOUND}</Error>
            : visibleItems.map(article => {
              const { id, slug, title, publishDate, body, categories, featuredImage } = article.node
              return (
                <ArticleGrid
                  key={id}
                  slug={slug}
                  title={title}
                  publishDate={publishDate}
                  body={body}
                  categories={categories}
                  featuredImage={featuredImage}
                />
              )
            })
          }
          {visibleItems && !(articleList.length === visibleItems.length) &&
            !(filteredItems.length === visibleItems.length) &&
            showLoadMore && (
            <LoadMoreButton>
              <button type="button" onClick={this.loadMore}>
                {loadMoreLabel}
                <ButtonArrow viewBox="0 0 12 21"><ArrowRight /></ButtonArrow>
              </button>
            </LoadMoreButton>
          )}
        </ArticleContainer>
      </Container>
    )
  }
}

const ArticleGridContainer = ({ loadMoreLabel }) => {
  return (
    <div>
      <StaticQuery
        query={graphql`
          query {
            allContentfulArticle (
              filter: {
                slug: {
                  nin: ["", null]
                }
                title: {
                  nin: ["", null]
                }
                node_locale: {
                  eq: "en-US"
                }
              }
              sort: { fields: [publishDate], order: DESC }
              limit: 10000
            ) {
              edges {
                node {
                  id
                  title
                  slug
                  publishDate(formatString: "YYYY/MM/DD")
                  publishYear: publishDate(formatString:"YYYY")
                  categories {
                    title
                  }
                  featuredImage {
                    imageFile {
                      fluid(maxWidth: 700) {
                        base64
                        aspectRatio
                        src
                        srcSet
                        sizes
                      }
                    }
                  }
                  body {
                    childMarkdownRemark {
                      rawMarkdownBody
                    }
                  }
                }
              }
            }
          }
        `}
        render={({ allContentfulArticle }) => {
          if (allContentfulArticle) {
            const allArticles = allContentfulArticle.edges
            const dateFilterItems = removeDuplicates(Array.from(new Set(allArticles.map((article) => {
              return {
                type: 'date',
                value: article.node.publishYear,
              }
            }))), 'value')

            const categoryFilterItems = removeDuplicates(Array.from(new Set(allArticles.map((article) => {
              if (article.node.categories) {
                return {
                  type: 'category',
                  value: article.node.categories.title,
                }
              }
            }))), 'value')

            return (
              <ArticleItems
                articleList={allContentfulArticle}
                loadMoreLabel={loadMoreLabel}
                dateFilter={dateFilterItems}
                categoryFilter={categoryFilterItems}
              />
            )
          }
        }}
      />
    </div>
  )
}

export default ArticleGridContainer

export const ArticleGridContainerFragment = graphql`
  fragment ArticleGridContainerFragment on ContentfulArticleGrid {
    loadMoreLabel
  }
`

ArticleGridContainer.propTypes = {
  loadMoreLabel: PropTypes.string,
}

ArticleGridContainer.defaultProps = {
  loadMoreLabel: 'Load More',
}

ArticleItems.propTypes = {
  articleList: PropTypes.shape({
    edges: PropTypes.arrayOf(PropTypes.shape({
      node: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        categories: PropTypes.shape({
          title: PropTypes.string,
        }).isRequired,
        featuredImage: PropTypes.shape({
          fluid: PropTypes.object,
        }),
        title: PropTypes.string,
        body: PropTypes.shape({
          childMarkdownRemark: PropTypes.shape({
            excerpt: PropTypes.string,
          }),
        }),
        publishDate: PropTypes.string.isRequired,
      }),
    })),
  }).isRequired,
  loadMoreLabel: PropTypes.string.isRequired,
  dateFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  categoryFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
}
